/**
 * Daily Brief desktop placement decision (Automations v2 §7).
 *
 * The Brief / notifications ActivityCenter has three mutually-exclusive
 * placements plus a mobile-modal fallback. This pure, table-tested helper is the
 * ONE source of truth for which one is live, so the Revise 50/50 layout REUSES
 * the split-brief plumbing instead of forking it:
 *
 *   • companion  — desktop solo chat (no module pane, not revising): the Brief
 *                  docks as the top third of the chat panel, rendered inside
 *                  `.conversation-chat-panel`.
 *   • split      — portal `.chat-brief-pane` INTO `#chat-box-root` so the
 *                  chat-box grid places it. True in TWO desktop-only states:
 *                    - a module pane is open (`isSplitViewActive`, top-third dock
 *                      via `:has(.chat-brief-pane)`), or
 *                    - a Revise session is active (`reviseActive`, the 50/50 left
 *                      column via `.chat-box.is-revise-split`, 15-revise-split.css).
 *                  `isSplitViewActive` already implies ≥768px; `reviseActive` does
 *                  NOT, so it is desktop-gated here (mobile revise keeps the modal).
 *   • (neither)  — mobile / narrow: the caller's centered-modal fallback.
 *
 * `showBriefCompanion` and `wantSplitBrief` are EXCLUSIVE: the companion must
 * never double-render while the split portal is mounted — that both empties the
 * grid's `activity` column AND paints a second ActivityCenter outside `.chat-box`.
 */
export interface BriefPlacementInput {
  /** Any non-closed Brief dock state (the Brief is mounted). */
  briefOpen: boolean;
  /** ChatDockContext.isSplitViewActive — module pane visible && desktop ≥768px. */
  isSplitViewActive: boolean;
  /** Desktop split viewport (MQ.desktopSplit, ≥768px). */
  isDesktopViewport: boolean;
  /** A Revise-with-AI session is live (ReviseSessionContext). */
  reviseActive: boolean;
}

export interface BriefPlacement {
  /** Render the solo companion card inside `.conversation-chat-panel`. */
  showBriefCompanion: boolean;
  /** Portal `.chat-brief-pane` into the chat-box grid (`#chat-box-root`). */
  wantSplitBrief: boolean;
}

export function computeBriefPlacement({
  briefOpen,
  isSplitViewActive,
  isDesktopViewport,
  reviseActive,
}: BriefPlacementInput): BriefPlacement {
  // Revise reuses the split portal (desktop only); the module-pane split already
  // implies desktop via isSplitViewActive.
  const wantSplitBrief =
    briefOpen && (isSplitViewActive || (reviseActive && isDesktopViewport));
  // The solo companion yields to BOTH split states so it never double-renders
  // (matches the original `isDesktopViewport && !isSplitViewActive`, minus the
  // revise state). briefOpen is applied by the caller, as before.
  const showBriefCompanion =
    isDesktopViewport && !isSplitViewActive && !reviseActive;
  return { showBriefCompanion, wantSplitBrief };
}
